import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  FileText,
  PencilLine,
  RotateCcw,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { differenceInCalendarDays, parseISO, format } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Container } from "@/platform/layout/container";
import { Button } from "@/platform/ui/button";
import { Badge } from "@/platform/ui/badge";
import { Card } from "@/platform/ui/card";
import { useProfile } from "@/platform/auth/use-profile";
import { useSession } from "@/platform/auth/use-session";
import { fetchCurrentWeekPlan } from "@/tenants/einstein/features/study-plan/api";
import type { StudyPlan } from "@/tenants/einstein/features/study-plan/types";
import { countDueReviews } from "@/tenants/einstein/features/review/api";
import { ClockSpot, TargetSpot, TrophySpot } from "@/platform/ui/illustrations/spot";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import { SubscriptionBanner } from "@/platform/billing/subscription-banner";

type Tone = "violet" | "pink" | "mint" | "yellow" | "coral" | "sky";

const TONE_BG: Record<Tone, string> = {
  violet: "bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent-soft-foreground)]",
  pink: "bg-[color:var(--color-pink-soft)] text-[color:var(--color-pink-deep)]",
  mint: "bg-[color:var(--color-mint-soft)] text-[color:var(--color-mint-deep)]",
  yellow: "bg-[color:var(--color-yellow-soft)] text-[color:var(--color-yellow-deep)]",
  coral: "bg-[color:var(--color-coral-soft)] text-[color:var(--color-coral-deep)]",
  sky: "bg-[color:var(--color-sky-soft)] text-[color:var(--color-sky-deep)]",
};

export function DashboardPage() {
  const { profile } = useProfile();
  const { session } = useSession();
  const [plan, setPlan] = useState<StudyPlan | null>(null);
  const [dueReviews, setDueReviews] = useState(0);

  useEffect(() => {
    if (!session) return;
    fetchCurrentWeekPlan(session.user.id).then(setPlan);
    countDueReviews(session.user.id).then(setDueReviews);
  }, [session]);

  const examDate = profile?.exam_date ? parseISO(profile.exam_date) : null;
  const daysToExam = examDate ? Math.max(0, differenceInCalendarDays(examDate, new Date())) : null;
  const weeklyHours = profile?.weekly_hours ?? 10;
  const firstName = profile?.display_name ?? profile?.full_name?.split(" ")[0] ?? "estudante";

  return (
    <div className="relative overflow-hidden bg-mesh">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-25 blur-2xl"
      />
      <SoftStar
        color="pink"
        className="pointer-events-none absolute right-10 top-32 w-16 opacity-50 animate-float"
      />

      <Container size="wide" className="relative py-12 md:py-16">
        {/* Subscription banner — soft gate */}
        <div className="mb-8 animate-fade-up">
          <SubscriptionBanner tenant="einstein" />
        </div>

        {/* HERO */}
        <header className="animate-fade-up grid gap-6 md:grid-cols-[1fr_auto] md:items-end">
          <div>
            <Badge variant="accentSolid" className="w-fit gap-2">
              <Sparkles className="size-3" aria-hidden />
              Painel
            </Badge>
            <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
              Oi, <span className="text-gradient-accent">{firstName}</span>. Bom te ver.
            </h1>
            <p className="mt-2 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
              Aqui está o resumo do seu contexto e o que mover a próxima nota.
            </p>
          </div>
          {daysToExam != null ? (
            <div className="flex items-center gap-4 rounded-xl bg-gradient-accent p-5 text-white shadow-[var(--shadow-accent)]">
              <TrophySpot className="size-14" />
              <div>
                <p
                  data-tabular="true"
                  className="font-display text-[length:var(--text-display-md)] font-bold leading-none tracking-[var(--text-display-md--letter-spacing)]"
                >
                  {daysToExam}
                </p>
                <p className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] opacity-85">
                  dias até a prova
                </p>
              </div>
            </div>
          ) : null}
        </header>

        {/* METRICS */}
        <section className="mt-10 grid gap-5 md:grid-cols-3">
          <MetricCard
            spot={<TargetSpot className="size-12 -ml-1" />}
            tone="violet"
            label="Curso-alvo"
            primary="Psicologia"
            secondary="Einstein · Banca Vunesp"
          />
          <MetricCard
            spot={<ClockSpot className="size-12 -ml-1" />}
            tone="mint"
            label="Horas/semana"
            primary={`${weeklyHours}h`}
            secondary="Plano calibra automaticamente"
          />
          <MetricCard
            spot={<TrophySpot className="size-12 -ml-1" />}
            tone="yellow"
            label="Prova prevista"
            primary={examDate ? format(examDate, "d MMM", { locale: ptBR }) : "Não definida"}
            secondary={examDate ? format(examDate, "yyyy", { locale: ptBR }) : undefined}
          />
        </section>

        {/* HOJE NO PLANO */}
        <section className="mt-8">
          {plan ? (
            <TodayPlanHighlight plan={plan} dueReviews={dueReviews} />
          ) : (
            <EmptyDiagnosisCTA />
          )}
        </section>

        {/* AÇÕES RÁPIDAS */}
        <section className="mt-8 grid gap-5 md:grid-cols-3">
          <ActionCard
            icon={PencilLine}
            tone="pink"
            kicker="Redação Vunesp"
            title="Correção em 30s"
            body="Texto digitado ou foto do manuscrito. Rubrica oficial em 4 critérios."
            to="/redacao"
          />
          <ActionCard
            icon={FileText}
            tone="mint"
            kicker="Discursivas"
            title="6 pontos cada"
            body="Bio · Quím · Fís · Mat · Inglês com rubrica por disciplina."
            to="/discursivas"
          />
          <ActionCard
            icon={RotateCcw}
            tone="coral"
            kicker="Revisão SM-2"
            title={dueReviews > 0 ? `${dueReviews} na fila` : "Fila vazia"}
            body="Agendada automaticamente a partir das suas tentativas."
            to="/revisar"
            highlighted={dueReviews > 0}
          />
        </section>
      </Container>
    </div>
  );
}

function EmptyDiagnosisCTA() {
  return (
    <Card className="relative overflow-hidden bg-gradient-accent p-10 md:p-14 text-white border-transparent shadow-[var(--shadow-accent)]">
      <Blob color="pink" className="pointer-events-none absolute -right-20 -top-20 w-72 opacity-30 blur-2xl" />
      <Blob color="yellow" className="pointer-events-none absolute -left-16 -bottom-16 w-56 opacity-25 blur-2xl" />
      <div className="relative grid items-center gap-8 md:grid-cols-[1fr_auto]">
        <div>
          <Badge variant="onDark">
            <Sparkles className="size-3" aria-hidden />
            Próximo passo
          </Badge>
          <h2 className="mt-4 font-display text-[length:var(--text-h1)] font-semibold leading-[var(--text-h1--line-height)] tracking-[var(--text-h1--letter-spacing)] text-balance">
            Faça o diagnóstico inicial.
          </h2>
          <p className="mt-3 max-w-2xl text-[length:var(--text-body)] text-white/85 text-pretty">
            Duas horas, foto honesta. Antes do diagnóstico não conseguimos calibrar a curva de
            evolução nem prever sua nota padronizada.
          </p>
        </div>
        <div className="flex flex-col items-center gap-4">
          <TargetSpot className="size-32 drop-shadow-xl animate-float" />
          <Button asChild size="lg" variant="onDark">
            <Link to="/diagnostico/sessao">
              Começar
              <ArrowUpRight className="size-4" aria-hidden />
            </Link>
          </Button>
        </div>
      </div>
    </Card>
  );
}

function TodayPlanHighlight({ plan, dueReviews }: { plan: StudyPlan; dueReviews: number }) {
  const today = new Date().toISOString().slice(0, 10);
  const todayPlan = plan.days.find((d) => d.date === today);
  const next = plan.days.find((d) => d.date >= today && d.blocks.length > 0);
  const day = todayPlan ?? next;
  const isToday = day?.date === today;

  return (
    <Card className="overflow-hidden p-0">
      <div className="grid gap-px bg-[color:var(--color-border)] md:grid-cols-[1.4fr_1fr]">
        <div className="bg-[color:var(--color-card)] p-8 md:p-10">
          <Badge variant="accentSolid" className="w-fit">
            <Sparkles className="size-3" aria-hidden />
            Plano da semana
          </Badge>
          <h2 className="mt-5 font-display text-[length:var(--text-h1)] font-semibold leading-[var(--text-h1--line-height)] tracking-[var(--text-h1--letter-spacing)] text-balance">
            {isToday && day
              ? `Hoje · ${day.blocks.length} bloco${day.blocks.length === 1 ? "" : "s"} · ${day.total_min} min`
              : day
                ? `Próximo bloco · ${format(parseISO(day.date), "EEEE", { locale: ptBR })}`
                : "Sem blocos pra hoje."}
          </h2>
          <p className="mt-3 max-w-md text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
            Gerado a partir do seu último diagnóstico cruzado com horas/semana.
            {dueReviews > 0
              ? ` ${dueReviews} questão${dueReviews === 1 ? "" : "s"} aguarda${dueReviews === 1 ? "" : "m"} revisão.`
              : ""}
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/plano">
                Ver plano completo
                <ArrowUpRight className="size-4" aria-hidden />
              </Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to="/diagnostico/sessao">Refazer diagnóstico</Link>
            </Button>
          </div>
        </div>

        <div className="bg-[color:var(--color-paper)] p-8 md:p-10">
          <p className="text-[length:var(--text-overline)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
            Blocos
          </p>
          <ul className="mt-4 space-y-2.5">
            {(day?.blocks ?? []).slice(0, 4).map((b, i) => (
              <li
                key={b.id}
                className="group flex items-center gap-3 rounded-md bg-[color:var(--color-card)] border border-[--color-border] px-3.5 py-2.5 animate-fade-up shadow-[var(--shadow-xs)]"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <span
                  data-tabular="true"
                  className="font-mono text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)] w-5"
                >
                  0{i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-[length:var(--text-body-sm)] font-medium">
                    {b.discipline ?? b.kind}
                  </p>
                  <p className="truncate text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                    {b.kind} · {b.duration_min} min
                  </p>
                </div>
                {b.link ? (
                  <Link
                    to={b.link}
                    aria-label={`Executar ${b.discipline ?? b.kind}`}
                    className="grid size-8 place-items-center rounded-full bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent)] transition-all group-hover:bg-[color:var(--color-accent)] group-hover:text-[color:var(--color-accent-foreground)]"
                  >
                    <ArrowUpRight className="size-3.5" aria-hidden />
                  </Link>
                ) : null}
              </li>
            ))}
            {(day?.blocks?.length ?? 0) === 0 ? (
              <li className="text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                Sem blocos agendados.
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    </Card>
  );
}

function MetricCard({
  spot,
  tone,
  label,
  primary,
  secondary,
}: {
  spot: React.ReactNode;
  tone: Tone;
  label: string;
  primary: string;
  secondary?: string;
}) {
  return (
    <Card className="lift p-6">
      <div className="flex items-start justify-between">
        <div>
          {spot}
          <Badge variant={tone === "violet" ? "accent" : (tone as "pink" | "mint" | "yellow" | "coral" | "sky")} className="mt-3">
            {label}
          </Badge>
        </div>
      </div>
      <p
        data-tabular="true"
        className="mt-4 font-display text-[length:var(--text-h2)] font-bold leading-none tracking-[var(--text-h2--letter-spacing)]"
      >
        {primary}
      </p>
      {secondary ? (
        <p className="mt-2 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
          {secondary}
        </p>
      ) : null}
    </Card>
  );
}

function ActionCard({
  icon: Icon,
  tone,
  kicker,
  title,
  body,
  to,
  highlighted,
}: {
  icon: LucideIcon;
  tone: Tone;
  kicker: string;
  title: string;
  body: string;
  to: string;
  highlighted?: boolean;
}) {
  return (
    <Link
      to={to}
      className={
        "group lift block rounded-xl border bg-[color:var(--color-card)] p-6 shadow-[var(--shadow-sm)] transition-colors " +
        (highlighted
          ? "border-[--color-accent]"
          : "border-[--color-border] hover:border-[--color-accent]/40")
      }
    >
      <div className="flex items-start justify-between">
        <span className={`chip size-11 ${TONE_BG[tone]}`}>
          <Icon className="size-5" aria-hidden />
        </span>
        <ArrowUpRight className="size-4 text-[color:var(--color-muted-foreground)] transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[color:var(--color-accent)]" aria-hidden />
      </div>
      <p className="mt-5 text-[length:var(--text-overline)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
        {kicker}
      </p>
      <p className="mt-1 font-display text-[length:var(--text-h3)] font-semibold leading-[var(--text-h3--line-height)] tracking-[var(--text-h3--letter-spacing)]">
        {title}
      </p>
      <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] text-pretty">
        {body}
      </p>
    </Link>
  );
}
